import { useEffect, useRef, useState } from 'preact/hooks'
import { CANONICAL_SIZE, PREVIEW_SIZE, SNAP_STEP } from '../constants'
import type { RenderOptions } from '../icon/IconBuilder'
import { IconPreview } from '../icon/IconPreview'
import type { Content, IconConfig } from '../types'
import { computeImageBox } from '../utils/image'
import { inputChecked } from '../utils/input'
import { snapTo } from '../utils/math'

const CANONICAL_CENTER = CANONICAL_SIZE / 2

const hitTest = (item: Content, cx: number, cy: number): boolean => {
  if (item.type === 'emoji') {
    const half = item.size / 2

    return (
      cx >= CANONICAL_CENTER + item.x - half &&
      cx <= CANONICAL_CENTER + item.x + half &&
      cy >= CANONICAL_CENTER + item.y - half &&
      cy <= CANONICAL_CENTER + item.y + half
    )
  }

  const box = computeImageBox(item.image, item, CANONICAL_CENTER, CANONICAL_CENTER)

  return cx >= box.x && cx <= box.x + box.w && cy >= box.y && cy <= box.y + box.h
}

interface Props {
  config: IconConfig
  onContentsChange: (contents: IconConfig['contents']) => void
}

interface DragState {
  index: number
  startX: number
  startY: number
  originX: number
  originY: number
}

export function Preview({ config, onContentsChange }: Props) {
  const [showSafeZone, setShowSafeZone] = useState<boolean>(true)
  const [snapToGrid, setSnapToGrid] = useState<boolean>(false)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const dragState = useRef<DragState | null>(null)

  const onPointerDown = (event: PointerEvent) => {
    const canvas = canvasRef.current

    if (!canvas) {
      return
    }

    const rect = canvas.getBoundingClientRect()
    const scale = CANONICAL_SIZE / PREVIEW_SIZE
    const cx = (event.clientX - rect.left) * scale
    const cy = (event.clientY - rect.top) * scale

    for (let i = config.contents.length - 1; i >= 0; i--) {
      const item = config.contents[i]

      if (item && hitTest(item, cx, cy)) {
        canvas.setPointerCapture(event.pointerId)
        dragState.current = {
          index: i,
          startX: event.clientX,
          startY: event.clientY,
          originX: item.x,
          originY: item.y,
        }
        break
      }
    }
  }

  const onPointerMove = (event: PointerEvent) => {
    const state = dragState.current

    if (state) {
      const scale = CANONICAL_SIZE / PREVIEW_SIZE
      const deltaX = (event.clientX - state.startX) * scale
      const deltaY = (event.clientY - state.startY) * scale

      let nextX = Math.round(state.originX + deltaX)
      let nextY = Math.round(state.originY + deltaY)

      if (snapToGrid) {
        nextX = snapTo(nextX, SNAP_STEP)
        nextY = snapTo(nextY, SNAP_STEP)
      }

      const target = config.contents[state.index]

      if (target && (nextX !== target.x || nextY !== target.y)) {
        onContentsChange(config.contents.map((item, i) => (i === state.index ? { ...item, x: nextX, y: nextY } : item)))
      }
    }
  }

  const onPointerUp = (event: PointerEvent) => {
    dragState.current = null

    const canvas = canvasRef.current

    if (canvas?.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId)
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')

    if (!canvas || !context) {
      return
    }

    const dpr = window.devicePixelRatio || 1
    const backingSize = Math.round(PREVIEW_SIZE * dpr)

    if (canvas.width !== backingSize) {
      canvas.width = backingSize
      canvas.height = backingSize
    }

    const preview = new IconPreview(config)
    let cancelled = false

    const draw = (options: RenderOptions) => {
      if (!cancelled) {
        preview.drawTo(context, backingSize, { ...options, showSafeZone, showGrid: snapToGrid })
      }
    }

    draw({})
    preview.resolveOptions().then(draw)

    return () => {
      cancelled = true
    }
  }, [config, showSafeZone, snapToGrid])

  return (
    <div class="preview-stack">
      <div class="preview-wrap">
        <canvas
          ref={canvasRef}
          class="preview-canvas"
          style={{ width: `${PREVIEW_SIZE}px`, height: `${PREVIEW_SIZE}px` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
      <div class="preview-toggles">
        <label class="toggle">
          <input type="checkbox" checked={showSafeZone} onChange={(event) => setShowSafeZone(inputChecked(event))} />
          Safe zone
        </label>
        <label class="toggle">
          <input type="checkbox" checked={snapToGrid} onChange={(event) => setSnapToGrid(inputChecked(event))} />
          Snap to grid
        </label>
      </div>
    </div>
  )
}
